import { useNavigate } from "react-router-dom";
import { brl, fileUrl } from "@/lib/api";
import { FileDown, FileText, Package } from "lucide-react";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/panel/Pagination";
import { Badge, Button, DataTable } from "@/components/ui-kit";

// Era um mapa de classes cruas (bg-emerald-50/bg-amber-50/bg-red-50); virou tom
// semântico do Badge, com o mesmo significado de negócio de antes:
// verde = autorizada · âmbar = processando · vermelho só para falha real,
// porque aí a nota não vale como documento fiscal.
// `enviada`/`pendente`, `denegada`, `erro_autorizacao` e `erro` estavam fora do
// mapa antigo e caíam no cinza neutro — o cliente via uma recusa da SEFAZ com a
// mesma cor de um estado sem informação. Mesmos tons de admin/Fiscal.js.
const INVOICE_TONE = {
  autorizada: "success",
  processando: "warning",
  enviada: "warning",
  pendente: "warning",
  cancelada: "danger",
  rejeitada: "danger",
  denegada: "danger",
  erro_autorizacao: "danger",
  erro: "danger",
};

export default function MyInvoices() {
  const navigate = useNavigate();
  // Paginação no servidor: antes só as 100 notas mais recentes chegavam à tela.
  const {
    items: invoices, total, page, lastPage, setPage,
    isLoading: loading, isError: error, refetch: retry,
  } = usePaginatedList("/invoices/my");

  return (
    /* Sem PageHeader: esta tela é uma aba dentro do AccountLayout, que já
       imprime o <h1> "Minha conta" e a aba "Notas fiscais". Um segundo h1 aqui
       duplicaria o título da página para leitor de tela. */
    <div className="space-y-4" data-testid="my-invoices-page">
      <DataTable
        rowTestId="my-invoice-row"
        errorMessage="Não foi possível carregar suas notas fiscais."
        caption="Notas fiscais dos seus pedidos"
        rows={invoices}
        rowKey={(i) => i.id}
        loading={loading}
        error={error}
        onRetry={retry}
        empty={{
          icon: FileText,
          title: "Nenhuma nota fiscal ainda",
          desc: "A nota é emitida pela loja depois que o pedido é faturado. Quando sair, ela aparece aqui com PDF e XML para download.",
          // Sem filtro nesta tela, a saída útil é o lugar onde a nota nasce: o pedido.
          action: <Button variant="secondary" icon={Package} onClick={() => navigate("/conta/pedidos")}>Ver meus pedidos</Button>,
          testId: "invoices-empty-msg",
        }}
        columns={[
          {
            key: "nota", header: "Nota", mono: true, className: "font-semibold",
            cell: (i) => `NF-e ${i.number}`,
          },
          { key: "pedido", header: "Pedido", mono: true, className: "text-slate-500", cell: (i) => i.source_number },
          {
            key: "data", header: "Data", className: "text-slate-500",
            cell: (i) => new Date(i.created_at).toLocaleDateString("pt-BR"),
          },
          { key: "total", header: "Total", align: "right", mono: true, cell: (i) => brl(i.total) },
          {
            key: "status", header: "Status",
            // O status vem cru do banco e tem valor com underscore
            // (`erro_autorizacao`); sem a troca por espaço o cliente lia
            // "Erro_autorizacao". Era o que o StatusBadge antigo já fazia.
            cell: (i) => (
              <Badge tone={INVOICE_TONE[i.status] || "neutral"} className="capitalize">
                {String(i.status).replace(/_/g, " ")}
              </Badge>
            ),
          },
          {
            // `acoes` é a chave que o DataTable reconhece para mandar a coluna
            // ao rodapé do cartão no celular — é onde a 2ª via precisa estar.
            key: "acoes", header: "2ª via", align: "right", className: "whitespace-nowrap",
            cell: (i) => (
              <div className="inline-flex items-center gap-3">
                <a href={fileUrl(`/invoices/${i.id}/pdf`)} target="_blank" rel="noreferrer" data-testid="my-invoice-pdf"
                  aria-label={`Baixar PDF da nota NF-e ${i.number}`}
                  className="text-brand-700 hover:underline text-xs font-semibold inline-flex items-center gap-1"><FileDown size={13} aria-hidden /> PDF</a>
                <a href={fileUrl(`/invoices/${i.id}/xml`)} target="_blank" rel="noreferrer" data-testid="my-invoice-xml"
                  aria-label={`Baixar XML da nota NF-e ${i.number}`}
                  className="text-slate-500 hover:underline text-xs font-mono">XML</a>
              </div>
            ),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="notas fiscais" />
    </div>
  );
}
